import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  Alert,
  Box,
  Button,
  Chip
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

const SelectionCriteria = ({
  formData,
  onFormChange,
  jsonErrors,
  isComplete = false,
  onSelectionCriteriaChange
}) => {
  const [vendorIds, setVendorIds] = useState([]);
  const [newVendorId, setNewVendorId] = useState("");
  const [joroSubcategoryIds, setJoroSubcategoryIds] = useState([]);
  const [newSubcategoryId, setNewSubcategoryId] = useState("");
  const [joroCategoryIds, setJoroCategoryIds] = useState([]);
  const [newJoroCategoryId, setNewJoroCategoryId] = useState("");

  // Parse existing JSON data when component mounts or formData changes
  useEffect(() => {
    try {
      const parsed = JSON.parse(formData.selectionCriteria || "{}");
      const vendorIdData = parsed.vendorId;
      if (Array.isArray(vendorIdData)) {
        setVendorIds(vendorIdData.map(id => String(id)));
      } else if (vendorIdData) {
        setVendorIds([String(vendorIdData)]);
      } else {
        setVendorIds([]);
      }
      setJoroSubcategoryIds((parsed.joroSubcategoryId || []).map(id => String(id)));
      setJoroCategoryIds((parsed.joroCategoryId || []).map(id => String(id)));
    } catch (error) {
      // If parsing fails, start with empty values
      setVendorIds([]);
      setJoroSubcategoryIds([]);
      setJoroCategoryIds([]);
    }
  }, [formData.selectionCriteria]);

  const addVendorId = () => {
    if (newVendorId.trim() && !vendorIds.includes(newVendorId.trim())) {
      const updatedVendorIds = [...vendorIds, newVendorId.trim()];
      setVendorIds(updatedVendorIds);
      setNewVendorId("");
      updateSelectionCriteria(updatedVendorIds, joroSubcategoryIds, joroCategoryIds);
    }
  };

  const removeVendorId = (index) => {
    const updatedVendorIds = vendorIds.filter((_, i) => i !== index);
    setVendorIds(updatedVendorIds);
    updateSelectionCriteria(updatedVendorIds, joroSubcategoryIds, joroCategoryIds);
  };

  const addSubcategoryId = () => {
    if (newSubcategoryId.trim() && !joroSubcategoryIds.includes(newSubcategoryId.trim())) {
      const updatedSubcategoryIds = [...joroSubcategoryIds, newSubcategoryId.trim()];
      setJoroSubcategoryIds(updatedSubcategoryIds);
      setNewSubcategoryId("");
      updateSelectionCriteria(vendorIds, updatedSubcategoryIds, joroCategoryIds);
    }
  };

  const removeSubcategoryId = (index) => {
    const updatedSubcategoryIds = joroSubcategoryIds.filter((_, i) => i !== index);
    setJoroSubcategoryIds(updatedSubcategoryIds);
    updateSelectionCriteria(vendorIds, updatedSubcategoryIds, joroCategoryIds);
  };

  const addJoroCategoryId = () => {
    if (newJoroCategoryId.trim() && !joroCategoryIds.includes(newJoroCategoryId.trim())) {
      const updatedJoroCategoryIds = [...joroCategoryIds, newJoroCategoryId.trim()];
      setJoroCategoryIds(updatedJoroCategoryIds);
      setNewJoroCategoryId("");
      updateSelectionCriteria(vendorIds, joroSubcategoryIds, updatedJoroCategoryIds);
    }
  };

  const removeJoroCategoryId = (index) => {
    const updatedJoroCategoryIds = joroCategoryIds.filter((_, i) => i !== index);
    setJoroCategoryIds(updatedJoroCategoryIds);
    updateSelectionCriteria(vendorIds, joroSubcategoryIds, updatedJoroCategoryIds);
  };

  const updateSelectionCriteria = (vendorIdValues, subcategoryIds, categoryIds) => {
    const selectionCriteria = {};

    // Only add vendorId if array has values, convert to integers
    if (vendorIdValues.length > 0) {
      const parsed = vendorIdValues.map(id => parseInt(id, 10)).filter(id => !isNaN(id));
      if (parsed.length > 0) {
        selectionCriteria.vendorId = parsed;
      }
    }

    // Only add joroSubcategoryId if array has values, convert to integers
    if (subcategoryIds.length > 0) {
      const parsed = subcategoryIds.map(id => parseInt(id, 10)).filter(id => !isNaN(id));
      if (parsed.length > 0) {
        selectionCriteria.joroSubcategoryId = parsed;
      }
    }

    // Only add joroCategoryId if array has values, convert to integers
    if (categoryIds.length > 0) {
      const parsed = categoryIds.map(id => parseInt(id, 10)).filter(id => !isNaN(id));
      if (parsed.length > 0) {
        selectionCriteria.joroCategoryId = parsed;
      }
    }

    // Convert to JSON string and update form
    const jsonString = JSON.stringify(selectionCriteria, null, 2);
    onSelectionCriteriaChange(jsonString);
  };

  const handleVendorKeyPress = (e) => {
    if (e.key === "Enter") {
      addVendorId();
    }
  };

  const handleCategoryKeyPress = (e) => {
    if (e.key === "Enter") {
      addJoroCategoryId();
    }
  };

  const handleSubcategoryKeyPress = (e) => {
    if (e.key === "Enter") {
      addSubcategoryId();
    }
  };

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="subtitle1">Selection Criteria</Typography>
          {isComplete && <CheckCircleIcon color="success" />}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Configure vendor and subcategory selection criteria for transaction matching.
        </Typography>
        {/* Vendor IDs */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Vendor IDs
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <TextField
              label="Add Vendor ID"
              value={newVendorId}
              onChange={(e) => setNewVendorId(e.target.value)}
              onKeyPress={handleVendorKeyPress}
              placeholder="Enter vendor ID"
              size="small"
              sx={{ flexGrow: 1 }}
            />
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addVendorId}
              disabled={!newVendorId.trim()}
              size="small"
            >
              Add
            </Button>
          </Box>
          {/* Display existing vendor IDs */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {vendorIds.map((id, index) => (
              <Chip
                key={index}
                label={id}
                onDelete={() => removeVendorId(index)}
                deleteIcon={<DeleteIcon />}
                variant="outlined"
                color="primary"
              />
            ))}
            {vendorIds.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No vendor IDs added yet
              </Typography>
            )}
          </Box>
        </Box>

        {/* Joro Category IDs */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Joro Category IDs
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <TextField
              label="Add Category ID"
              value={newJoroCategoryId}
              onChange={(e) => setNewJoroCategoryId(e.target.value)}
              onKeyPress={handleCategoryKeyPress}
              placeholder="Enter category ID"
              size="small"
              sx={{ flexGrow: 1 }}
            />
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addJoroCategoryId}
              disabled={!newJoroCategoryId.trim()}
              size="small"
            >
              Add
            </Button>
          </Box>
          {/* Display existing category IDs */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {joroCategoryIds.map((id, index) => (
              <Chip
                key={index}
                label={id}
                onDelete={() => removeJoroCategoryId(index)}
                deleteIcon={<DeleteIcon />}
                variant="outlined"
                color="primary"
              />
            ))}
            {joroCategoryIds.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No category IDs added yet
              </Typography>
            )}
          </Box>
        </Box>

        {/* Joro Subcategory IDs */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Joro Subcategory IDs
          </Typography>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <TextField
              label="Add Subcategory ID"
              value={newSubcategoryId}
              onChange={(e) => setNewSubcategoryId(e.target.value)}
              onKeyPress={handleSubcategoryKeyPress}
              placeholder="Enter subcategory ID"
              size="small"
              sx={{ flexGrow: 1 }}
            />
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addSubcategoryId}
              disabled={!newSubcategoryId.trim()}
              size="small"
            >
              Add
            </Button>
          </Box>
          {/* Display existing subcategory IDs */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {joroSubcategoryIds.map((id, index) => (
              <Chip
                key={index}
                label={id}
                onDelete={() => removeSubcategoryId(index)}
                deleteIcon={<DeleteIcon />}
                variant="outlined"
                color="primary"
              />
            ))}
            {joroSubcategoryIds.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No subcategory IDs added yet
              </Typography>
            )}
          </Box>

        </Box>

        {jsonErrors.selectionCriteria && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Invalid selection criteria format
          </Alert>
        )}
      </AccordionDetails>
    </Accordion >
  );
};

export default SelectionCriteria;
